import { useCallback, useEffect, useRef, useState } from 'react';
import type { GetExperimentExposureActiveQueryParams } from '@gonfalon/openapi/operations/getExperimentExposureActive';

import { makeRefetchInterval } from './makeRefetchInterval';
import { useExperimentExposureActive } from './useExperimentExposureActive';

/** Surface the troubleshooting helper after 2 minutes of polling with no event. */
export const TROUBLESHOOTING_THRESHOLD_MS = 120000;
const ELAPSED_TICK_MS = 1000;

type UseExperimentExposureActivePollingOptions = {
  enabled?: boolean;
  /**
   * Maximum total fetch attempts (initial fetch + retries) before polling stops.
   * The initial fetch counts as attempt 1, so maxAttempts=3 means 1 initial + 2 retries.
   */
  maxAttempts?: number;
  maxDurationMs?: number;
  onStop?: () => void;
  /**
   * Keep polling after a failed request instead of stopping. A transient error
   * during a long wait shouldn't permanently halt the check, and a later
   * successful poll clears the error on its own. No effect in maxAttempts/maxDurationMs modes.
   */
  keepPollingOnError?: boolean;
  /**
   * Track elapsed time since polling started and expose it as `elapsedMs`, ticking
   * once a second. Opt-in so callers that don't render time-based copy pay no
   * per-second re-render cost. Resets whenever the query params change.
   */
  reportElapsed?: boolean;
};

const PLACEHOLDER_QUERY: GetExperimentExposureActiveQueryParams = {
  flagKey: '',
  eventKey: '',
  metricKey: '',
  experimentKey: '',
};

export function useExperimentExposureActivePolling(
  query: GetExperimentExposureActiveQueryParams | undefined,
  projectKey: string,
  environmentKey: string,
  {
    enabled = true,
    maxAttempts,
    maxDurationMs,
    onStop,
    keepPollingOnError = false,
    reportElapsed = false,
  }: UseExperimentExposureActivePollingOptions = {},
) {
  const attemptCountRef = useRef(0);
  // 0 means uninitialized; the duration check is skipped until the timer starts.
  const startTimeRef = useRef(0);
  // Whether we've already logged the current run of consecutive errors, so
  // keepPollingOnError doesn't emit telemetry on every poll while an error persists.
  const errorStreakRef = useRef(false);
  const onStopRef = useRef(onStop);
  onStopRef.current = onStop;

  useEffect(() => {
    if (enabled && maxDurationMs !== undefined) {
      startTimeRef.current = Date.now();
    }
  }, [enabled, maxDurationMs]);

  const elapsedStartRef = useRef(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (!enabled || !reportElapsed) {
      return undefined;
    }
    elapsedStartRef.current = Date.now();
    const id = setInterval(() => {
      setElapsedMs(Date.now() - elapsedStartRef.current);
    }, ELAPSED_TICK_MS);
    return () => clearInterval(id);
  }, [enabled, reportElapsed]);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch: queryRefetch,
  } = useExperimentExposureActive(
    { query: query ?? PLACEHOLDER_QUERY, projectKey, environmentKey },
    {
      enabled,
      retry: false,
      refetchInterval: makeRefetchInterval({
        onStopRef,
        attemptCountRef,
        startTimeRef,
        errorStreakRef,
        maxAttempts,
        maxDurationMs,
        keepPollingOnError,
        errorLabel: 'Error polling for experiment exposure activation',
      }),
    },
  );

  useEffect(() => {
    if (maxAttempts !== undefined && isFetching) {
      attemptCountRef.current += 1;
    }
  }, [isFetching, maxAttempts]);

  const refetch = useCallback(() => {
    if (maxDurationMs !== undefined) {
      startTimeRef.current = Date.now();
    }
    return queryRefetch();
  }, [maxDurationMs, queryRefetch]);

  return {
    data,
    isLoading,
    error,
    refetch,
    isActive: !!data?.flagActive && !!data?.metricActive,
    elapsedMs,
  };
}
